<template>
  <v-bottom-sheet v-model="dialogVisible" max-width="1920" class="fire-detail-dialog">
    <div class="dialog-wrapper">
      <!-- 标题 -->
      <div class="dialog-title">电表详情</div>

      <!-- 主内容区域 -->
      <div class="dialog-main">
        <!-- 左侧：电表数据内容区 -->
        <div class="left-content">
          <div v-if="loading" class="d-flex justify-center align-center" style="height: 100%">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="!fireData || fireData.length === 0" class="empty-text">
            暂无数据
          </div>
          <div v-else class="fire-list">
            <!-- 遍历电表，变量名先不改了，本页面是直接从FireDetailDialog复制过来的 -->
            <div v-for="(elink, index) in fireData" :key="elink.elink_id || index" class="fire-array-panel"
              :class="{ 'highlight-device': highlightedDevice.uuid == (elink.uuid ?? index) }"
              :id="'meter-' + (elink.uuid ?? index)">

              <!-- 折叠面板标题栏 -->
              <div class="array-header" @click="togglePanel(index)">
                <div class="array-title">

                  <!-- 图标可以用图片或者 mdi-icon，这里用渐变色块模拟截图 -->
                  <div class="header-icon-box">
                    <img src="@/assets/images/station-monitor/detail-dialog/panel-header-icon.png" width="14"
                      height="14" />
                  </div>
                  <div class="ml-4">{{ elink.meterName }}</div>
                  <div class="ml-4">uuid: {{ elink.uuid }}</div>
                  <div class="ml-4">型号: {{ elink.productModel ?? '--' }}</div>
                </div>
                <div class="header-right">
                  <v-chip :color="isOnline(elink) ? 'green' : 'red'"> {{ isOnline(elink) ? '在线' : '离线' }}</v-chip>
                  <span class="header-time" v-if="elink.record_time">
                    采样时间: {{ formatTime(elink.record_time) }}
                  </span>
                  <img src="@/assets/images/station-monitor/detail-dialog/collapse-icon.png" class="collapse-icon"
                    :class="{ 'expanded': !collapsedPanels[index] }" alt="展开/收起" />
                </div>
              </div>

              <!-- 展开内容 -->
              <transition name="panel-expand">
                <div v-show="!collapsedPanels[index]" class="array-content-wrapper">
                  <div class="array-content">
                    <!-- 信息卡片 -->
                    <div class="elink-info-card">
                      <!-- 电表内容 -->
                      <MeterCard :data="elink" />
                    </div>
                    <!-- 右侧：eBlock 表格 -->


                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>

        <!-- 右侧：设备树 -->
        <div class="right-sidebar">
          <!-- 搜索框 -->
          <div class="search-wrapper">
            <v-text-field v-model="searchText" placeholder="按设备名称/UUID搜索设备" rounded variant="outlined" density="compact"
              append-inner-icon="mdi-magnify" clearable hide-details class="search-input"></v-text-field>
          </div>

          <!-- 设备树 -->
          <div class="device-tree">
            <v-treeview :items="treeItems" :search="searchText" :custom-filter="filterDevice" item-value="id"
              item-title="name" activatable open-all density="compact" class="custom-treeview"
              @update:activated="handleTreeItemClick">
              <template v-slot:prepend="{ item }">
                <img v-if="item.type === 'root'" src="@/assets/images/station-monitor/detail-dialog/tree-root-icon.png"
                  style="width: 16px; height: 14px;" alt="根节点" loading="lazy" />
                <img v-else-if="item.type === 'station'"
                  src="@/assets/images/station-monitor/detail-dialog/tree-station-icon.png"
                  style="width: 16px; height: 14px;" alt="电站" loading="lazy" />
                <img v-else-if="item.type === 'category'"
                  src="@/assets/images/station-monitor/detail-dialog/tree-category-icon.png"
                  style="width: 16px; height: 14px;" alt="分类" loading="lazy" />
                <img v-else src="@/assets/images/station-monitor/detail-dialog/tree-device-icon.png"
                  style="width: 16px; height: 14px;" alt="设备" loading="lazy" />
              </template>
            </v-treeview>
          </div>
        </div>
      </div>

      <!-- 关闭按钮 -->
      <v-btn icon size="small" variant="text" class="close-btn" @click="closeDialog">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
  </v-bottom-sheet>

  <!-- Pack 详情弹框 (新组件) -->
  <FirePackDetailDialog v-model="packDialogVisible" :block="currentBlock" />

  <!-- 消防趋势图弹框 -->
  <FireChartDialog v-model="fireChartDialogVisible" :station-id="currentStationId" :elink-uuid="currentElinkUuid"
    :initial-field="currentChartField" />
</template>

<script setup>
import { ref, computed, watch, onUnmounted, shallowRef } from 'vue'
import { ws_req } from '@/ws/wss-request'
import useUserStore from '@/store/modules/user'
import { useStationContext } from '../../composables/useStationContext'
import FirePackDetailDialog from './FirePackDetailDialog.vue'
import FireChartDialog from './FireChartDialog.vue'
import dayjs from 'dayjs'
import MeterCard from '../meter-card/index.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const searchText = ref('')
const loading = ref(false)
const fireData = shallowRef([])
// 存储折叠状态，key为 index, value为 true(折叠)
const collapsedPanels = ref({})
let wsTimer = null

// 使用 composable 获取电站上下文信息
const { stationId: currentStationId, stationName: currentStationName } = useStationContext()

// 树结构
const treeItems = shallowRef([])
const highlightedDevice = ref({ type: null, uuid: null })

// Pack详情
const packDialogVisible = ref(false)
const currentBlock = ref(null)
const packList = ref([])

// 趋势图详情
const fireChartDialogVisible = ref(false)
const currentElinkUuid = ref('')
const currentChartField = ref('temp')

// 打开趋势图
const openChartDialog = (elink, field) => {
  if (!elink || !elink.uuid) {
    console.warn('eLink UUID missing', elink)
    return
  }
  currentElinkUuid.value = elink.uuid
  currentChartField.value = field
  fireChartDialogVisible.value = true
}


// 切换折叠状态
const togglePanel = (index) => {
  collapsedPanels.value[index] = !collapsedPanels.value[index]
}

// 格式化时间
const formatTime = (time) => {
  if (!time) return '--'
  return dayjs(time).format('YYYY-MM-DD HH:mm:ss')
}



const isOnline = (data) => {
  return typeof data.i_a === 'number'
}

// WS请求
const fetchFireData = async (recurs = true) => {
  clearTimeout(wsTimer)

  if (!currentStationId.value) return

  try {
    if (!ws_req.is_open && !ws_req.is_connecting) {
      const userStore = useUserStore()
      await userStore.getInfo()
    }

    let result_mt = await ws_req.request('ws_meter', {
      stationId: currentStationId.value
    })
    let result_pv = await ws_req.request('ws_meter_pv', {
      stationId: currentStationId.value
    })
    // 合并meter和meter_pv
    const result = result_mt.concat(result_pv)
    console.log('result', result)

    if (result && Array.isArray(result)) {
      result.forEach(item => {
        if (!item.data) item.data = {}
      })
      fireData.value = result

      if (treeItems.value.length === 0 || treeItems.value[0]?.children?.length !== result.length) {
        buildTreeItems(result)
      }
    }
  } catch (e) {
    console.error('Fire Sys WS request failed:', e)
  }

  if (recurs && dialogVisible.value) {
    wsTimer = setTimeout(() => {
      fetchFireData(true)
    }, 5000)
  }
}

// 构建树结构
const buildTreeItems = (data) => {
  if (!data || !Array.isArray(data)) return

  console.log('data', data);


  const children = data.map((item, index) => {
    // 确保树节点名称显示 elink_name
    const arrayName = item.meterName
    const elinkName = item.elink_name || `${index + 1}#能量链`
    // 使用 elink_id 如果存在，否则使用 index 作为唯一标识的一部分
    const uniqueElinkId = item.uuid ?? index
    const blocks = item.data?.cabinet || []

    return {
      id: `${uniqueElinkId}`,
      name: arrayName, // 外层显示 "1#储能方阵"
      type: 'meter',
    }
  })

  treeItems.value = [
    {
      id: 'station-root',
      name: currentStationName.value,
      type: 'station',
      children: children
    }
  ]
}

// 树点击处理
const handleTreeItemClick = (selectedItems) => {
  const id = selectedItems.toString()

  // 设置高亮
  highlightedDevice.value = {
    type: null,
    uuid: selectedItems.toString()
  }
  // 滚动到选中项

  const element = document.querySelector(`#meter-${id}`)
  element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const filterDevice = (value, search, item) => {
  if (!search) return true
  const searchLower = search.toLowerCase()
  const text = (value || '').toLowerCase()
  const rawItem = item.raw || item
  const uuid = (rawItem.uuid || '').toLowerCase()
  return text.includes(searchLower) || uuid.includes(searchLower)
}

const closeDialog = () => {
  dialogVisible.value = false
}

watch(dialogVisible, (newVal) => {
  if (newVal && currentStationId.value) {
    loading.value = true
    fetchFireData(true).finally(() => { loading.value = false })
  } else {
    clearTimeout(wsTimer)
  }
})

watch(currentStationId, (newVal) => {
  if (newVal && dialogVisible.value) {
    fetchFireData(true)
  }
})

onUnmounted(() => {
  clearTimeout(wsTimer)
})


// const meterList = ref([])
// let t = null
// async function fetchMeterData(recur) {
//   if (currentStationId.value) {
//     let result_mt = await ws_req.request('ws_meter', {
//       stationId: currentStationId.value
//     })
//     let result_pv = await ws_req.request('ws_meter_pv', {
//       stationId: currentStationId.value
//     })
//     // 合并meter和meter_pv
//     const result = result_mt.concat(result_pv)
//     // if (Array.isArray(result)) {
//     //   result.forEach(data => {
//     //     data.Time = dayjs(data.receive_time).format('YYYY-MM-DD HH:mm:ss')
//     //   })
//     // }
//     console.log('result', result)
//     meterList.value = result.filter(item => {
//       return item.meterType != 2
//     }).sort((a, b) => {
//       return b.meterType - a.meterType
//     })
//     console.log('meterList', meterList.value)
//   }
//   if (recur) {
//     wsTimer = setTimeout(() => {
//       fetchMeterData(recur)
//     }, 5 * 1000);
//   }
// }


// fetchMeterData(true)













</script>

<style scoped lang="scss">
.fire-detail-dialog,
.fire-pack-detail-dialog {
  :deep(.v-overlay__content) {
    width: 100vw !important;
    max-width: 1920px !important;
    height: 950px !important;
    margin: 0 auto !important;
    border-radius: 30px 30px 0 0 !important;
  }
}

.dialog-wrapper {
  position: relative;
  width: 100%;
  height: 950px;
  padding: 20px 24px 0;
  background-color: #fff !important;
  background: url('@/assets/images/station-monitor/detail-dialog/detail-bg.svg') no-repeat center center;
  background-size: cover;
  border-radius: 30px 30px 0 0 !important;
  display: flex;
  flex-direction: column;
}

.dialog-title {
  // height: 80px;
  display: flex;
  align-items: center;
  padding: 0 40px 0 24px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
  position: relative;
  flex-shrink: 0;
  margin-bottom: 20;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 24px;
  z-index: 10;
}

.dialog-main {
  display: flex;
  gap: 30px;
  height: calc(100% - 143px);
  /* 143 = 80header + padding? Adjust as needed matching ClusterDetail styling context */
  background: rgba(246, 251, 255, 1);
  border-radius: 30px;
  padding: 20px;
  margin-bottom: 20px;
  overflow: hidden;
}

/* Reuse table styles from ClusterDetailTable */
.card-bg {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 104.73%);
}

.card-bd {
  border: 1px solid #fff;
}

.custom-table {
  :deep(th) {
    font-size: 14px !important;
    height: 48px !important;
    border-bottom: 1px solid #E0E4E7 !important;
    background-color: transparent !important;
  }

  :deep(td) {
    font-size: 14px !important;
    height: 52px !important;
    border-bottom: 1px solid #E0E4E7 !important;
  }
}

.header-pointer {
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
}

.text-normal {
  color: #444a6d;
  /* default text color */
}

.dialog-main {
  display: flex;
  gap: 30px;
  height: calc(100% - 83px);
  background: rgba(246, 251, 255, 1);
  border-radius: 30px;
  padding: 20px;
}

.left-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  padding-right: 5px;
  padding: 16px;
}

/* 方阵面板 */
.fire-array-panel {
  background: rgba(255, 255, 255, 1);
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 16px;
}

.array-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 22px;
  cursor: pointer;
  user-select: none;
  background: #F5F8FF;
  // border-bottom: 1px solid #f0f0f0;

  // &:hover {
  //   background-color: rgba(246, 251, 255, 0.5);
  // }
}

.array-title {
  font-size: 14px;
  color: rgba(46, 46, 46, 1);
  // font-weight: bold;
  display: flex;
  align-items: center;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-time {
  font-size: 12px;
  color: #666;
}

.collapse-icon {
  width: 11px;
  height: 6px;
  transition: transform 0.3s ease;

  &.expanded {
    transform: rotate(180deg);
  }
}

.array-content-wrapper {
  padding: 20px 22px;
}

.array-content {
  display: flex;
  gap: 18px;
  align-items: stretch;
}

/* 左侧 eLink 卡片 */
.elink-info-card {
  /* 加宽以适应双列布局 */
  flex-shrink: 0;
  flex-grow: 1;
  background: #fff;
  /* 改为白色底，或者保持设计稿可能有的极浅色 */
  // border: 1px solid #F4F7FC;
  border-radius: 8px;
  // padding: 12px;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.3s ease;

}

.highlight-device {
  animation: highlightBlink 0.5s ease-in-out 6;
}

@keyframes highlightBlink {

  0%,
  100% {
    border-color: rgba(244, 247, 252, 1);
    box-shadow: none;
  }

  50% {
    border-color: rgba(58, 104, 255, 1);
    box-shadow: 0 0 12px rgba(58, 104, 255, 0.5);
  }
}

.elink-header {
  height: 48px;
  margin-bottom: 12px;
  background: rgba(239, 244, 251, 0.7);
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  align-items: center;

  .elink-icon-title {
    display: flex;
    align-items: center;

    .header-icon-box {
      margin-right: 8px;
      display: flex;
      align-items: center;
      justify-content: start;
    }

    .elink-name {
      font-weight: 400;
      font-size: 14px;
      color: rgba(46, 46, 46, 1);
    }
  }
}

.elink-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  /* 间距 */
  margin-bottom: 12px;
}

/* 每个信息项的小盒子 */
.info-item-box {
  background: rgba(244, 247, 252, 0.6);
  /* 浅灰背景 */
  border-radius: 6px;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 64px;

  &.empty-box {
    background: transparent;
  }

  .label {
    font-size: 12px;
    color: rgba(46, 46, 46, 1);
    font-weight: 300;
    margin-bottom: 8px;
  }

  .value {
    font-size: 14px;
    color: rgba(46, 46, 46, 1);
    display: flex;
    align-items: center;
    font-weight: 400;

    .text-value {
      margin-left: 2px;
    }
  }
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;

  &.success {
    background-color: #52C41A;
  }

  &.error {
    background-color: #FF4D4F;
  }

  &.normal {
    background-color: #ccc;
  }
}

.no-data-overlay {
  position: absolute;
  top: 48px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fff;
  color: #999;
  font-size: 14px;
  z-index: 5;
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
}

.version-info {
  background: transparent;
  padding: 0;
  margin-top: 16px;

  .version-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding: 0 14px;
    height: 42px;
    background: rgba(244, 247, 252, 0.6);
    border-radius: 5px;

    &:last-child {
      margin-bottom: 0;
    }

    .version-label {
      color: rgba(46, 46, 46, 1);
      font-weight: 300;
      font-size: 12px;
    }

    .version-value {
      color: rgba(46, 46, 46, 1);
      font-weight: 400;
      font-size: 14px;
    }
  }
}

/* 右侧 eBlock 表格 */
.eblock-table-container {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  // border: 1px solid #F4F7FC;
  overflow: hidden;

  :deep(.v-table) {
    .v-table__wrapper {
      border-radius: 8px;
    }
  }
}

.custom-table {
  font-size: 14px;

  thead th {
    background-color: #fff !important;
    /* 表头白色或极浅色 */
    color: rgba(150, 165, 184, 1) !important;
    font-weight: 400 !important;
    font-size: 12px;
    white-space: nowrap;
    height: 48px !important;
    border-bottom: none !important;
    box-shadow: none !important;
  }

  tbody td {
    height: 52px !important;
    white-space: nowrap;
    color: #444A6D !important;
    border-bottom: 1px solid rgba(237, 242, 246, 1) !important;

    span {
      color: #444A6D !important;
    }
  }

  /* 固定首列 */
  thead th.fixed-col,
  tbody td.fixed-col {
    position: sticky !important;
    left: 0 !important;
    z-index: 2;
    /* 内容层级 */
    background-color: #fff !important;
    box-shadow: 7px 2px 11.8px 0 rgba(0, 0, 0, 0.04) !important;
    border-right: none;
  }

  /* 固定尾列 */
  thead th.fixed-col-right,
  tbody td.fixed-col-right {
    position: sticky !important;
    right: 0 !important;
    z-index: 2;
    background-color: #fff !important;
    box-shadow: -7px 2px 11.8px 0 rgba(0, 0, 0, 0.04) !important;
    border-left: none;
  }

  /* 提升固定列表头的层级 */
  thead th.fixed-col,
  thead th.fixed-col-right {
    z-index: 3 !important;
    /* 表头高于内容 */
  }
}

.status-badge {
  padding: 4px 12px;
  border-radius: 37px;
  font-size: 14px;
  display: inline-block;

  &.online {
    background: #DDF3D6;
    color: #36B60C;
  }

  &.offline {
    background: #F4F7FC;
    color: #6B6C6D;
  }
}

.action-link {
  color: #3A68FF !important;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.highlight-row {
  background-color: #E6F7FF !important;
}

.right-sidebar {
  width: 319px;
  background: rgba(255, 255, 255, 1);
  border-radius: 20px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.search-wrapper {
  position: relative;
  margin-bottom: 26px;
}

.device-tree {
  flex: 1;
  overflow-y: auto;
}

/* Animation */
.panel-expand-enter-active,
.panel-expand-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  opacity: 1;
  overflow: hidden;
}

.panel-expand-enter-from,
.panel-expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.mt {
  display: flex;
}

.mt-before {
  width: 100px;
  border: 1px solid blue;
}

.mt-after {
  width: 100px;
  border: 1px solid blue;
}

.mt-table {
  table {
    border-collapse: collapse;
  }

  tbody tr {
    height: 60px;
    background: #ccc;
    border: 1px solid green;
  }

  tbody :is(tr, td) {
    width: 120px;
  }
}
</style>
